import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearSession, getSessionUsername } from '../config/authConfig';
import { treeRegistry } from '../config/treeRegistry';
import Diamond from '../components/Diamond';
import LocationTree from '../components/LocationTree';
import LaunchTree from '../components/LaunchTree';
import SpendingTree from '../components/SpendingTree';
import InteractiveTree from '../components/InteractiveTree';
import StaticTreeView from '../components/StaticTreeView';
import DynamicTree from '../components/DynamicTree';

const Dashboard = () => {
  const navigate = useNavigate();
  const [activeTree, setActiveTree] = useState(null);
  const [viewMode, setViewMode] = useState(null); // 'walkthrough' or 'static'
  const [savedTrees, setSavedTrees] = useState([]);
  const [loadingTrees, setLoadingTrees] = useState(true);
  const [activeDynamicTree, setActiveDynamicTree] = useState(null);
  const username = getSessionUsername();

  // Load saved trees on mount
  useEffect(() => {
    const fetchSavedTrees = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trees`);
        if (response.ok) {
          const data = await response.json();
          setSavedTrees(data.trees || []);
        }
      } catch (e) {
        console.error("Failed to fetch saved trees:", e);
      } finally {
        setLoadingTrees(false);
      }
    };
    
    fetchSavedTrees();
  }, []);

  const handleLogout = () => {
    clearSession();
    navigate('/login');
  };

  const openTree = (treeId, mode) => {
    setActiveTree(treeId);
    setViewMode(mode);
  };

  const openDynamicTree = (treeData) => {
    setActiveDynamicTree(treeData);
  };

  const closeTree = () => {
    setActiveTree(null);
    setViewMode(null);
    setActiveDynamicTree(null);
  };

  // Render the appropriate tree based on selection
  const renderTreeModal = () => {
    // Dynamic tree from saved AI sessions
    if (activeDynamicTree) {
      return <DynamicTree treeData={activeDynamicTree} onClose={closeTree} />;
    }
    
    if (!activeTree) return null;

    if (viewMode === 'walkthrough') {
      switch (activeTree) {
        case 'location':
          return <LocationTree onClose={closeTree} />;
        case 'launch':
          return <LaunchTree onClose={closeTree} />;
        case 'spending':
          return <SpendingTree onClose={closeTree} />;
        case 'mini':
          return (
            <div className="tree-modal-overlay">
              <div className="tree-modal-container">
                <button className="tree-modal-close" onClick={closeTree} aria-label="Close">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
                <div className="tree-modal-header">
                  <h1 className="tree-modal-title">
                    <Diamond color="#0057FF" size="md" /> Binary Game Introduction
                  </h1>
                  <p className="tree-modal-subtitle">INTERACTIVE DECISION TREE</p>
                </div>
                <div className="tree-modal-content">
                  <InteractiveTree />
                </div>
              </div>
            </div>
          );
        default:
          return null;
      }
    }

    if (viewMode === 'static') {
      return <StaticTreeView treeId={activeTree} onClose={closeTree} />;
    }

    return null;
  };

  return (
    <div className="dashboard-page" data-testid="dashboard-page">
      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-header-content">
          <h1 className="dashboard-title">
            <Diamond color="#0057FF" size="md" />
            <span>Dashboard</span>
          </h1>
          <button 
            onClick={handleLogout} 
            className="dashboard-logout"
            data-testid="logout-button"
          >
            Log Out
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-main">
        <div className="dashboard-container">
          {/* Binary Game Session Button - only for mhansell */}
          {username === 'mhansell' && (
            <button
              onClick={() => navigate('/binary-game')}
              data-testid="binary-game-start-btn"
              style={{
                background: '#FFFFFF',
                border: '0.5px solid #DDDDDD',
                borderLeft: '3px solid #0057FF',
                padding: '16px 20px',
                cursor: 'pointer',
                marginBottom: '32px',
                display: 'block',
              }}
            >
              <span style={{
                fontFamily: "'Geist Mono', 'IBM Plex Mono', monospace",
                fontSize: '6.5px',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: '#888888',
              }}>
                BINARY GAME | START SESSION
              </span>
            </button>
          )}

          {/* Section Header */}
          <div className="dashboard-section-header">
            <span className="dashboard-label">YOUR DECISION TREES</span>
            <h2 className="dashboard-heading">All Trees</h2>
            <p className="dashboard-subheading">Walk through interactively or view the full structure.</p>
          </div>

          {/* Tree Grid - Hand-built trees first */}
          <div className="dashboard-grid">
            {treeRegistry.map((tree) => (
              <div key={tree.id} className="dashboard-card" data-testid={`tree-card-${tree.id}`}>
                <div className="dashboard-card-header">
                  <span className="dashboard-card-category">{tree.category}</span>
                  <span className="dashboard-card-gates">{tree.gates} GATES</span>
                </div>
                
                <h3 className="dashboard-card-title">{tree.title}</h3>
                <p className="dashboard-card-description">{tree.description}</p>
                
                <div className="dashboard-card-actions">
                  <button
                    onClick={() => openTree(tree.id, 'walkthrough')}
                    className="dashboard-btn dashboard-btn-primary"
                    data-testid={`walkthrough-${tree.id}`}
                  >
                    <Diamond color="#FFFFFF" size="xs" />
                    Walk Through
                  </button>
                  <button
                    onClick={() => openTree(tree.id, 'static')}
                    className="dashboard-btn dashboard-btn-secondary"
                    data-testid={`view-full-${tree.id}`}
                  >
                    View Full Tree
                  </button>
                </div>
              </div>
            ))}
            
            {/* AI-Facilitated Trees - newest first */}
            {savedTrees.map((tree) => (
              <div key={tree.id} className="dashboard-card dashboard-card-ai" data-testid={`tree-card-${tree.id}`}>
                <div className="dashboard-card-header">
                  <span className="dashboard-card-category dashboard-card-ai-badge">AI-FACILITATED</span>
                  <span className="dashboard-card-gates">{tree.gates?.length || 0} GATES</span>
                </div>
                
                <h3 className="dashboard-card-title">{tree.title}</h3>
                <p className="dashboard-card-description">
                  {tree.result_text?.substring(0, 100)}{tree.result_text?.length > 100 ? '...' : ''}
                </p>
                <p className="dashboard-card-date">
                  {new Date(tree.created_at).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
                
                <div className="dashboard-card-actions">
                  <button
                    onClick={() => openDynamicTree(tree)}
                    className="dashboard-btn dashboard-btn-primary dashboard-btn-full"
                    data-testid={`walkthrough-ai-${tree.id}`}
                  >
                    <Diamond color="#FFFFFF" size="xs" />
                    Walk Through
                  </button>
                </div>
              </div>
            ))}
            
            {/* Loading state */}
            {loadingTrees && (
              <div className="dashboard-card dashboard-card-loading">
                <p>Loading saved trees...</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="dashboard-footer">
        <p>Binary Game Dashboard v0.1</p>
        <a href="/" className="dashboard-footer-link">← Back to Landing Page</a>
      </footer>

      {/* Tree Modals */}
      {renderTreeModal()}
    </div>
  );
};

export default Dashboard;
